import React, { useState } from 'react';
import { setWorkersSettings } from '../api';

/**
 * Налаштування машин парку з адмінки — те саме, що у вікні агента на машині
 * (127.0.0.1:8787): повзунок, «Робота», пауза при людині, розклад.
 *
 * ДІЄ ТЕ, ЩО ЗМІНИЛИ ОСТАННІМ — тут чи на машині, окремо по кожному розділу.
 * Тому на сервер ідуть ЛИШЕ розділи, які адмін тут справді чіпав: інакше
 * правка розкладу для десяти машин затерла б кожній її власний повзунок.
 *
 * Машина бере правку на найближчому стуку (10-20 с); вимкнена — коли
 * прокинеться; агент до випуску 69 — після оновлення.
 */

const GROUPS = [['weekday', "Робочі дні (понеділок — п'ятниця)"],
                ['weekend', 'Вихідні дні (субота — неділя)']];
const ROWS = ['Не запускати', 'Навантаження', 'Навантаження'];
const rules = () => [
  { on: false, pct: 0,   from: '09:00', to: '18:00' },
  { on: false, pct: 50,  from: '18:00', to: '22:00' },
  { on: false, pct: 100, from: '22:00', to: '09:00' },
];
// Типові значення агента (agent/state.py DEFAULTS) — для машини, що ще не
// звітувала своїх налаштувань.
const DEFAULTS = {
  throttle_pct: 100, paused: false,
  idle_pause: { enabled: true, resume_min: 10, busy_pct: 0 },
  schedule: { enabled: false, weekday: rules(), weekend: rules() },
};
const SECTION_NAMES = {
  throttle_pct: 'Віддавати машину', paused: 'Робота',
  idle_pause: 'Коли працює людина', schedule: 'Розклад',
};
const STATE_TEXT = {
  pending: 'чекає машину', applied: 'застосовано',
  local_newer: 'змінено на машині пізніше',
  refused: 'машина не дозволяє віддалене керування',
};

const clone = (v) => JSON.parse(JSON.stringify(v));

function initial(w) {
  const v = w?.settings?.values || {};
  const out = clone(DEFAULTS);
  for (const k of Object.keys(DEFAULTS)) {
    if (v[k] !== undefined && v[k] !== null) out[k] = clone(v[k]);
  }
  // Правка адміна, що ще не дійшла, — показуємо саме її: інакше друге
  // відкриття вікна «відкотило» б форму до старих значень машини.
  const adm = w?.settings?.admin || {};
  for (const [k, s] of Object.entries(adm)) {
    if (s.state === 'pending' && s.value !== undefined) out[k] = clone(s.value);
  }
  for (const g of ['weekday', 'weekend']) {
    if (!Array.isArray(out.schedule[g]) || out.schedule[g].length !== 3) {
      out.schedule[g] = rules();
    }
  }
  return out;
}

export default function NodeSettingsModal({ targets, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial(targets[0]));
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (section, value) => {
    setForm(f => ({ ...f, [section]: value }));
    setDirty(d => ({ ...d, [section]: true }));
  };
  const setRule = (g, i, field, value) => {
    const sc = clone(form.schedule);
    sc[g][i][field] = value;
    set('schedule', sc);
  };

  const old = targets.filter(w => !w.settings?.supported);
  // Людина за машиною зняла галочку «дозволити адміністратору…» у вікні агента
  // (агент 70+): правка ляже, але машина її не візьме, поки дозвіл не повернуть.
  const closed = targets.filter(w => w.settings?.remote?.known && !w.settings.remote.settings);
  const many = targets.length > 1;
  const dirtyKeys = Object.keys(dirty).filter(k => dirty[k]);

  const save = async () => {
    if (!dirtyKeys.length) { onClose(); return; }
    const patch = {};
    for (const k of dirtyKeys) patch[k] = form[k];
    setSaving(true); setErr('');
    try {
      await setWorkersSettings(targets.map(w => w.agent_id), patch);
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'не збережено');
    } finally {
      setSaving(false);
    }
  };

  const mark = (k) => dirty[k] ? <span style={S.dirty}> • змінено</span> : null;
  const sc = form.schedule;

  return (
    <div style={S.back} onClick={onClose}>
      <div style={S.box} onClick={e => e.stopPropagation()}>
        <div style={S.head}>
          <b>Налаштування {many ? `${targets.length} машин` : `«${targets[0].name}»`}</b>
          <button style={S.x} onClick={onClose}>✕</button>
        </div>
        {many && (
          <div style={S.note}>
            {targets.map(w => w.name).join(', ')}. Форма заповнена значеннями
            «{targets[0].name}»; на машини підуть лише розділи, які ви зміните тут.
          </div>
        )}
        {old.length > 0 && (
          <div style={S.warn}>
            {old.map(w => w.name).join(', ')}: агент старший за випуск 69 —
            налаштування застосуються після його оновлення.
          </div>
        )}
        {closed.length > 0 && (
          <div style={S.warn}>
            {closed.map(w => w.name).join(', ')}: людина за машиною не дозволила
            віддалене керування (галочка у вікні агента) — правка чекатиме, поки
            дозвіл не повернуть.
          </div>
        )}

        <div style={S.row}>
          <div style={S.lbl}>Віддавати машину{mark('throttle_pct')}</div>
          <div style={S.ctl}>
            <input type="range" min={0} max={100} step={5} value={form.throttle_pct}
                   onChange={e => set('throttle_pct', +e.target.value)}
                   style={{ flex: 1 }} />
            <b style={{ width: 48, textAlign: 'right' }}>{form.throttle_pct}%</b>
          </div>
        </div>

        <div style={S.row}>
          <div style={S.lbl}>Коли працює людина{mark('idle_pause')}</div>
          <div>
            <label>
              <input type="checkbox" checked={!!form.idle_pause.enabled}
                     onChange={e => set('idle_pause', { ...form.idle_pause, enabled: e.target.checked })} />
              {' '}Віддавати для агента{' '}
            </label>
            <input type="number" min={0} max={100} step={5} value={form.idle_pause.busy_pct ?? 0}
                   onChange={e => set('idle_pause', { ...form.idle_pause,
                     busy_pct: Math.max(0, Math.min(100, Math.round((+e.target.value || 0) / 5) * 5)) })}
                   style={S.num} />%, якщо машина зайнята, і поновлювати через{' '}
            <input type="number" min={1} max={240} value={form.idle_pause.resume_min}
                   onChange={e => set('idle_pause', { ...form.idle_pause,
                                                      resume_min: Math.max(1, +e.target.value || 10) })}
                   style={S.num} /> хв неактивності
            <div style={S.hint}>зайнята — рух мишки або натиснута клавіша на самій машині; 0 % — агент зупиняється. Діє з агента 74</div>
          </div>
        </div>

        <div style={S.row}>
          <div style={S.lbl}>Робота{mark('paused')}</div>
          <div style={S.ctl}>
            <button style={{ ...S.btn, ...(!form.paused ? S.on : null) }}
                    onClick={() => set('paused', false)}>Працювати</button>
            <button style={{ ...S.btn, ...(form.paused ? S.on : null) }}
                    onClick={() => set('paused', true)}>Зупинено</button>
          </div>
        </div>
        <div style={{ ...S.hint, margin: '-4px 0 8px 170px' }}>
          це кнопка у вікні самої машини; окремо від «Зупинити» в таблиці
        </div>

        <div style={S.row}>
          <div style={S.lbl}>Розклад{mark('schedule')}</div>
          <label>
            <input type="checkbox" checked={!!sc.enabled}
                   onChange={e => set('schedule', { ...sc, enabled: e.target.checked })} />
            {' '}увімкнути <span style={S.hint}>поза вказаними вікнами діє повзунок</span>
          </label>
        </div>
        <div style={{ opacity: sc.enabled ? 1 : 0.5 }}>
          {GROUPS.map(([g, title]) => (
            <div key={g}>
              <div style={S.grp}>{title}</div>
              {ROWS.map((label, i) => (
                <div key={i} style={S.srow}>
                  <label style={{ width: 150 }}>
                    <input type="checkbox" checked={!!sc[g][i].on}
                           onChange={e => setRule(g, i, 'on', e.target.checked)} /> {label}
                  </label>
                  {i === 0
                    ? <span style={{ ...S.num, display: 'inline-block', color: '#9CA3AF' }}>0</span>
                    : <input type="number" min={0} max={100} step={5} value={sc[g][i].pct}
                             onChange={e => setRule(g, i, 'pct', +e.target.value)} style={S.num} />}
                  % від
                  <input type="time" value={sc[g][i].from}
                         onChange={e => setRule(g, i, 'from', e.target.value)} />
                  до
                  <input type="time" value={sc[g][i].to}
                         onChange={e => setRule(g, i, 'to', e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>

        {!many && Object.keys(targets[0].settings?.admin || {}).length > 0 && (
          <div style={S.note}>
            Попередні правки з адмінки:{' '}
            {Object.entries(targets[0].settings.admin).map(([k, s]) =>
              `${SECTION_NAMES[k] || k} — ${STATE_TEXT[s.state] || s.state}`).join('; ')}
          </div>
        )}
        {err && <div style={S.warn}>{err}</div>}
        <div style={S.foot}>
          <button style={S.btn} onClick={onClose}>Скасувати</button>
          <button style={{ ...S.btn, ...S.on }} disabled={saving} onClick={save}>
            {saving ? 'Зберігаю…'
              : dirtyKeys.length ? `Застосувати (${dirtyKeys.length})` : 'Закрити'}
          </button>
        </div>
      </div>
    </div>
  );
}

const S = {
  back: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          overflowY: 'auto', padding: '40px 16px' },
  box:  { background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 720,
          fontSize: 14, color: '#1A1A1A', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 16, marginBottom: 12 },
  x:    { border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#5B646F' },
  note: { fontSize: 12, color: '#5B646F', background: '#F5F7FA', borderRadius: 6,
          padding: '8px 10px', margin: '8px 0' },
  warn: { fontSize: 12, color: '#92400E', background: '#FEF3C7', borderRadius: 6,
          padding: '8px 10px', margin: '8px 0' },
  row:  { display: 'flex', gap: 12, alignItems: 'flex-start', margin: '12px 0', flexWrap: 'wrap' },
  lbl:  { width: 158, color: '#5B646F', flexShrink: 0 },
  ctl:  { display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 200 },
  hint: { fontSize: 12, color: '#8A93A0' },
  dirty: { color: '#475FFA', fontSize: 11 },
  num:  { width: 56, padding: '3px 6px', margin: '0 4px', border: '1px solid #E5E7EB',
          borderRadius: 4, textAlign: 'right' },
  grp:  { fontSize: 13, fontWeight: 600, color: '#8A93A0', margin: '10px 0 4px' },
  srow: { display: 'flex', gap: 6, alignItems: 'center', margin: '4px 0', flexWrap: 'wrap' },
  btn:  { padding: '6px 14px', borderRadius: 6, border: '1px solid #E5E7EB',
          background: '#fff', cursor: 'pointer', fontSize: 13 },
  on:   { background: '#475FFA', color: '#fff', borderColor: '#475FFA' },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 },
};
